import React, { useCallback, useMemo, useState } from "react";
import type { FilterInterface, SortButtonChoice } from "@shopify/polaris";
import { Select, IndexFilters, ChoiceList } from "@shopify/polaris";
import { getCurrencySymbol } from "../../../helper_functions/utils";
import { allSuppliers, allTypes, allVendors, getParamValue, useUser } from "../../../api_utils/requests";
import { DualThumbRangeSlider } from "./DualThumbRangeSlider";
import MoreLessFilter from "../../common/Filters/MoreLessFilter";
import type { Option, SalesTrendsQueryParams } from "../../../api_utils/types";
import { useSalesTrendsFilters } from "../../../hooks/useSalesTrendsFilters";
import { useSearchParams } from "react-router-dom";
import TagCheckList from "../../common/Filters/TagCheckList";

const TABS = {
  sales_rate: 0,
  seasonal: 1,
};

const tabs = [
  {
    // sends collection=undiscarded param to backend
    id: "sales-rate-query",
    content: "Sales rate",
    accessibilityLabel: "Sales rate result",
    panelID: "sales-rate-tab-content",
  },
  {
    // sends collection=all param to backend
    id: "seasonal-sales-query",
    content: "Seasonal",
    accessibilityLabel: "Seasonal sales",
    panelID: "seasonal-sales-tab-content",
  },
];

const defaultSortOptions = [
  { label: "Title", directionLabel: "A-Z", value: "title asc" },
  { label: "Title", directionLabel: "Z-A", value: "title desc" },
  { label: "SKU", directionLabel: "A-Z", value: "sku asc" },
  { label: "SKU", directionLabel: "Z-A", value: "sku desc" },
  { label: "Vendor", directionLabel: "A-Z", value: "vendor asc" },
  { label: "Vendor", directionLabel: "Z-A", value: "vendor desc" },
  { label: "Type", directionLabel: "A-Z", value: "product_type asc" },
  { label: "Type", directionLabel: "Z-A", value: "product_type desc" },
  { label: "Available", directionLabel: "Small/Big", value: "currently_available asc" },
  { label: "Available", directionLabel: "Big/Small", value: "currently_available desc" },
  { label: "Quantity ordered", directionLabel: "Small/Big", value: "ordered_quantity asc" },
  { label: "Quantity ordered", directionLabel: "Big/Small", value: "ordered_quantity desc" },
  { label: "Days available", directionLabel: "Small/Big", value: "available_days asc" },
  { label: "Days available", directionLabel: "Big/Small", value: "available_days desc" },
  { label: "Sales", directionLabel: "Small/Big", value: "sales_quantity asc" },
  { label: "Sales", directionLabel: "Big/Small", value: "sales_quantity desc" },
  { label: "Revenue", directionLabel: "Small/Big", value: "revenue asc" },
  { label: "Revenue", directionLabel: "Big/Small", value: "revenue desc" },
  { label: "Sales rate", directionLabel: "Small/Big", value: "sales_rate asc" },
  { label: "Sales rate", directionLabel: "Big/Small", value: "sales_rate desc" },
  { label: "Days remaining", directionLabel: "Small/Big", value: "estimated_days_of_sales_remaining asc" },
  { label: "Days remaining", directionLabel: "Big/Small", value: "estimated_days_of_sales_remaining desc" },
] as SortButtonChoice[];

export default function SalesTrendsSearchFieldAndMoreFilters({
  setFilters,
  maxSalesRate,
  forecastingMethod,
  setForecastingMethod,
}: {
  readonly setFilters: (filters: Partial<SalesTrendsQueryParams>) => void;
  readonly maxSalesRate: number;
  readonly forecastingMethod: number;
  readonly setForecastingMethod: (method: number) => void;
}): React.ReactElement {
  const [searchParams] = useSearchParams();
  const defaultSortColumn = forecastingMethod === TABS.seasonal ? "sales_quantity" : "revenue";
  const initSortedCol =
    getParamValue(searchParams.get("by_sort[column]"), defaultSortColumn)
  const defaultDirection = "desc";
  const initSortedColDir = getParamValue<string>(searchParams.get("by_sort[direction]"), defaultDirection);
  const [sortValue, setSortValue] = useState([`${initSortedCol} ${initSortedColDir}`]);

  const {
    data: {
      user: { currency: userCurrency },
    },
  } = useUser();

  // Memoize sort options to prevent unnecessary recalculations
  const sortOptions = useMemo(() => {
    if (forecastingMethod === TABS.seasonal) {
      return defaultSortOptions.filter((filter) => !filter.label.includes("Sales rate"));
    }
    return defaultSortOptions;
  }, [forecastingMethod]);

  const setFiltersWithSort = useCallback((filters: Partial<SalesTrendsQueryParams>) => {
      const [column_name, direction] = sortValue[0].split(" ");
      if (column_name === defaultSortColumn && direction === defaultDirection) {
        setFilters({
          ...filters,
          by_sort: undefined,
        });
      } else {
        setFilters({
          ...filters,
          by_sort: {
            column: column_name,
            direction: typeof direction === "number" ? "asc" : direction,
          },
        });
      }
    },
    [setFilters, sortValue, defaultSortColumn, defaultDirection],
  );

  const {
    supplierId,
    setSupplierId,
    suppliers,
    supplierFilterComponent,

    productType,
    setProductType,
    productTypes,
    productTypeFilterComponent,

    stockInfo,
    setStockInfo,
    stockInfoOptions,
    stockInfoFilterComponent,

    productTags,
    setProductTags,
    handleNewTagAddition,
    productTagsFilterComponent,

    shopifyChannel,
    setShopifyChannel,
    shopifyChannelOptions,
    shopifyChannelFilterComponent,

    vendor,
    setVendor,
    vendors,
    vendorFilterComponent,

    horseVariantArchiveStatus,
    setArchiveStatus,
    archiveStatusOptions,
    archivedStatusFilterComponent,

    discounted,
    setDiscounted,
    discountedOptions,
    discountedFilterComponent,

    orderedQuantity,
    setOrderedQuantity,
    orderedQuantityFilterComponent,

    cost,
    setCost,
    costFilterComponent,

    revenue,
    setRevenue,
    revenueFilterComponent,

    salesQuantity,
    setSalesQuantity,
    salesQuantityFilterComponent,

    salesRate,
    setSalesRate,
    salesRateFilterComponent,

    weight,
    setWeight,
    weightFilterComponent,

    currentlyAvailable,
    setCurrentlyAvailable,
    currentlyAvailableFilterComponent,

    availableDays,
    setAvailableDays,
    availableDaysFilterComponent,

    daysRemaining,
    setDaysRemaining,
    daysRemainingFilterComponent,

    search,
    setSearch,

    handleSearchRemove,
    handleFiltersClearAll,

    saleTrendsFilters,

    mode,
    setMode,
  } = useSalesTrendsFilters({
    setFilters: setFiltersWithSort,
  });

  archivedStatusFilterComponent.filter = (
    <Select
      id="sales_trends_archived_status_select"
      label=""
      labelHidden
      onChange={setArchiveStatus}
      options={archiveStatusOptions satisfies Option[]}
      value={horseVariantArchiveStatus}
    />
  );
  discountedFilterComponent.filter = (
    <Select
      id="sales_trends_discounted_select"
      label=""
      labelHidden
      onChange={setDiscounted}
      options={discountedOptions satisfies Option[]}
      value={discounted}
    />
  );
  availableDaysFilterComponent.filter = (
    <MoreLessFilter
      less_than={availableDays.less_than}
      more_than={availableDays.more_than}
      setStateCallback={setAvailableDays}
      targetState={availableDays}
      type="integer"
    />
  );
  costFilterComponent.filter = (
    <MoreLessFilter
      align="right"
      less_than={cost.less_than}
      more_than={cost.more_than}
      prefix={getCurrencySymbol(userCurrency)}
      setStateCallback={setCost}
      targetState={cost}
      type="currency"
    />
  );
  currentlyAvailableFilterComponent.filter = (
    <MoreLessFilter
      less_than={currentlyAvailable.less_than}
      more_than={currentlyAvailable.more_than}
      setStateCallback={setCurrentlyAvailable}
      targetState={currentlyAvailable}
      type="integer"
    />
  );
  daysRemainingFilterComponent.filter = (
    <MoreLessFilter
      less_than={daysRemaining.less_than}
      more_than={daysRemaining.more_than}
      setStateCallback={setDaysRemaining}
      targetState={daysRemaining}
      type="integer"
    />
  );
  orderedQuantityFilterComponent.filter = (
    <MoreLessFilter
      less_than={orderedQuantity.less_than}
      more_than={orderedQuantity.more_than}
      setStateCallback={setOrderedQuantity}
      targetState={orderedQuantity}
      type="integer"
    />
  );
  productTagsFilterComponent.filter = (
    <TagCheckList onAdd={handleNewTagAddition} onChange={setProductTags} selectedOptions={productTags} />
  );
  productTypeFilterComponent.filter = (
    <Select
      id="sales_trends_product_type_select"
      label=""
      labelHidden
      onChange={setProductType}
      options={productTypes satisfies Option[]}
      value={productType || allTypes}
    />
  );
  revenueFilterComponent.filter = (
    <MoreLessFilter
      align="right"
      less_than={revenue.less_than}
      more_than={revenue.more_than}
      prefix={getCurrencySymbol(userCurrency)}
      setStateCallback={setRevenue}
      targetState={revenue}
      type="currency"
    />
  );
  salesQuantityFilterComponent.filter = (
    <MoreLessFilter
      less_than={salesQuantity.less_than}
      more_than={salesQuantity.more_than}
      setStateCallback={setSalesQuantity}
      targetState={salesQuantity}
      type="currency"
    />
  );
  salesRateFilterComponent.filter = (
    <DualThumbRangeSlider
      defaultMax={1.9}
      maxSalesRate={maxSalesRate}
      rangeValue={salesRate}
      setRangeValue={setSalesRate}
      step={0.1}
      suffix="per day"
    />
  );
  shopifyChannelFilterComponent.filter = (
    <ChoiceList
      choices={shopifyChannelOptions}
      onChange={React.useCallback(
        ([value]) => {
          setShopifyChannel(value);
        },
        [setShopifyChannel],
      )}
      selected={[shopifyChannel]}
      title=""
      titleHidden
    />
  );
  stockInfoFilterComponent.filter = (
    <Select
      id="sales_trends_stock_info_select"
      label=""
      labelHidden
      onChange={setStockInfo}
      options={stockInfoOptions satisfies Option[]}
      value={stockInfo || "All"}
    />
  );
  supplierFilterComponent.filter = (
    <Select
      id="sales_trends_supplier_select"
      label=""
      labelHidden
      onChange={setSupplierId}
      options={suppliers satisfies Option[]}
      value={supplierId || allSuppliers}
    />
  );
  vendorFilterComponent.filter = (
    <Select
      id="sales_trends_vendor_select"
      label=""
      labelHidden
      onChange={setVendor}
      options={vendors satisfies Option[]}
      value={vendor || allVendors}
    />
  );
  weightFilterComponent.filter = (
    <MoreLessFilter
      less_than={weight.less_than}
      more_than={weight.more_than}
      setStateCallback={setWeight}
      suffix="g"
      targetState={weight}
      type="number"
    />
  );

  const filterComponents = [
    archivedStatusFilterComponent,
    currentlyAvailableFilterComponent,
    costFilterComponent,
    discountedFilterComponent,
    availableDaysFilterComponent,
    daysRemainingFilterComponent,
    orderedQuantityFilterComponent,
    productTypeFilterComponent,
    revenueFilterComponent,
    salesQuantityFilterComponent,
    salesRateFilterComponent,
    shopifyChannelFilterComponent,
    stockInfoFilterComponent,
    supplierFilterComponent,
    productTagsFilterComponent,
    vendorFilterComponent,
    weightFilterComponent,
  ] as FilterInterface[];

  return (
    <IndexFilters
      appliedFilters={saleTrendsFilters}
      canCreateNewView={false}
      cancelAction={{
        onAction: () => {},
        disabled: false,
        loading: false,
      }}
      filters={
        forecastingMethod === TABS.seasonal
          ? filterComponents.filter((filter) => filter.label !== "Sales rate")
          : filterComponents
      }
      mode={mode}
      onClearAll={handleFiltersClearAll}
      onQueryChange={setSearch}
      onQueryClear={handleSearchRemove}
      onSelect={setForecastingMethod}
      onSort={setSortValue}
      queryPlaceholder="Search variants"
      queryValue={search}
      selected={forecastingMethod}
      setMode={setMode}
      sortOptions={sortOptions}
      sortSelected={sortValue}
      tabs={tabs}
    />
  );
}
